import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";
import { usePaywall } from "@/features/paywall";
import { ApiError } from "@/lib/api";
import { SseError } from "@/lib/sse";

/**
 * What the UI should do about a failed menu generation / regeneration.
 *
 *  - `paywall`    — 402: free quota exhausted or premium required → open the paywall
 *  - `onboarding` — 409 "no household": the user must finish onboarding first
 *  - `conflict`   — 409 "already validated": the week is locked
 *  - `forbidden`  — 403: outside the generation window, or `draft_required`
 *                   (no server draft for that week)
 *  - `session`    — 401
 *  - `other`      — anything else (429 caps, 5xx, network, unexpected body)
 */
export type GenerationErrorKind =
  | "paywall"
  | "onboarding"
  | "conflict"
  | "forbidden"
  | "session"
  | "other";

export interface GenerationError {
  kind: GenerationErrorKind;
  /** HTTP status of the rejected request, or null for network errors. */
  status: number | null;
  /** Machine-readable `detail.code` from the backend, when present. */
  code: string | null;
  /** Translated, user-readable message. Never raw JSON. */
  message: string;
}

function detailOf(body: unknown): unknown {
  if (body && typeof body === "object" && "detail" in body) {
    return (body as { detail: unknown }).detail;
  }
  return body;
}

function codeOf(detail: unknown): string | null {
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code: unknown }).code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/** 409 from the generation gate when the user has no household row yet. */
function isMissingHousehold(detail: unknown): boolean {
  const code = codeOf(detail);
  if (code === "onboarding_required" || code === "household_missing") {
    return true;
  }
  return (
    typeof detail === "string" && /foyer|onboarding|household/i.test(detail)
  );
}

/**
 * Map an error thrown by `sseFromPost`, the `api()` helper (e.g. the
 * `/menu/regenerate-meal` swap) or `fetch` to a translated, actionable
 * {@link GenerationError}. Pure — no side effects.
 */
export function classifyGenerationError(
  error: unknown,
  t: TFunction,
): GenerationError {
  if (!(error instanceof SseError || error instanceof ApiError)) {
    return {
      kind: "other",
      status: null,
      code: null,
      message: t("generation_errors.network"),
    };
  }

  const detail = detailOf(error.body);
  const code = codeOf(detail);
  const base = { status: error.status, code };

  switch (error.status) {
    case 402:
      return {
        ...base,
        kind: "paywall",
        message:
          code === "premium_required"
            ? t("generation_errors.premium")
            : t("generation_errors.quota"),
      };
    case 409:
      return isMissingHousehold(detail)
        ? {
            ...base,
            kind: "onboarding",
            message: t("generation_errors.onboarding"),
          }
        : {
            ...base,
            kind: "conflict",
            message: t("generation_errors.already_validated"),
          };
    case 403:
      return {
        ...base,
        kind: "forbidden",
        message:
          code === "draft_required"
            ? t("generation_errors.draft_required")
            : t("generation_errors.window"),
      };
    case 429:
      return {
        ...base,
        kind: "other",
        message:
          code === "regen_limit_reached"
            ? t("generation_errors.regen_limit")
            : t("generation_errors.rate_limited"),
      };
    case 401:
      return {
        ...base,
        kind: "session",
        message: t("generation_errors.session"),
      };
    case 422:
      return {
        ...base,
        kind: "other",
        message: t("generation_errors.invalid"),
      };
    default:
      return {
        ...base,
        kind: "other",
        message:
          code === "ledger_unavailable"
            ? t("generation_errors.ledger_unavailable")
            : error.status >= 500
              ? t("generation_errors.unavailable")
              : t("generation_errors.unknown"),
      };
  }
}

/**
 * Returns a callback that classifies a generation error and performs the
 * matching side effect: 402 opens the paywall (read-only variant when the
 * free quota is exhausted), 409 "no household" redirects to onboarding.
 * The returned {@link GenerationError} carries the message to display.
 *
 * Must be used inside the Router and the PaywallProvider.
 */
export function useGenerationErrorHandler(): (
  error: unknown,
) => GenerationError {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openPaywall } = usePaywall();

  return useCallback(
    (error: unknown) => {
      const classified = classifyGenerationError(error, t);
      if (classified.kind === "paywall") {
        openPaywall(
          classified.code === "premium_required"
            ? { feature: t("generation_errors.paywall_feature") }
            : { variant: "read_only" },
        );
      } else if (classified.kind === "onboarding") {
        navigate("/onboarding");
      }
      return classified;
    },
    [t, navigate, openPaywall],
  );
}
